import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl, mockAuthHeaders } from '../lib/api';

interface SchedulerStatus {
  isPaused: boolean;
  pausedBy: string | null;
  pausedAt: string | null;
  pendingTriggers: number;
  scheduleVersion: number;
  connections: number;
}

export function useSchedulerStatus() {
  return useQuery<SchedulerStatus>({
    queryKey: ['scheduler-status'],
    queryFn: async () => {
      const response = await fetch(apiUrl('/api/v1/admin/scheduler-status'), {
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders(),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const detail = (errorBody as Record<string, Record<string, string>>)?.error?.message || response.statusText;
        throw new Error(detail);
      }

      const json = await response.json();
      return (json as Record<string, unknown>).data as SchedulerStatus;
    },
    refetchInterval: 10_000,
  });
}

export function useSchedulerPause() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paused: boolean) => {
      const response = await fetch(apiUrl('/api/v1/admin/scheduler-pause'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ paused }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const detail = (errorBody as Record<string, Record<string, string>>)?.error?.message || response.statusText;
        throw new Error(detail);
      }

      const json = await response.json();
      return (json as Record<string, unknown>).data as SchedulerStatus;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler-status'] });
    },
  });
}
